
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { formSchema, type FormValues } from "../schema/assessmentSchema";
import { useTranslations } from "@/hooks/useTranslations";
import { useLanguagePath } from "@/utils/language";

export const useAssessmentForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useTranslations("assessments");
  const { getPath } = useLanguagePath();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      assessment_date: new Date(),
    },
  });

  const onSubmit = async (values: FormValues) => {
    try {
      setIsSubmitting(true);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      const processedData = {
        student_id: values.student_id,
        trainer_id: user.id,
        assessment_date: values.assessment_date.toISOString().split('T')[0],
        weight: values.weight ? parseFloat(values.weight) : null,
        body_fat_percentage: values.body_fat_percentage ? parseFloat(values.body_fat_percentage) : null,
        muscle_mass: values.muscle_mass ? parseFloat(values.muscle_mass) : null,
        chest_measurement: values.chest_measurement ? parseFloat(values.chest_measurement) : null,
        waist_measurement: values.waist_measurement ? parseFloat(values.waist_measurement) : null,
        hip_measurement: values.hip_measurement ? parseFloat(values.hip_measurement) : null,
        arm_measurement: values.arm_measurement ? parseFloat(values.arm_measurement) : null,
        thigh_measurement: values.thigh_measurement ? parseFloat(values.thigh_measurement) : null,
        resting_heart_rate: values.resting_heart_rate ? parseInt(values.resting_heart_rate) : null,
        observations: values.observations,
      };

      if (id) {
        const { error } = await supabase
          .from("physical_assessments")
          .update(processedData)
          .eq("id", id);

        if (error) throw error;
        toast.success(t("notifications.updated"));
      } else {
        const { error } = await supabase
          .from("physical_assessments")
          .insert([processedData]);

        if (error) throw error;
        toast.success(t("notifications.created"));
      }

      queryClient.invalidateQueries({ queryKey: ["physical-assessments"] });
      navigate(getPath("/dashboard/assessments"));
    } catch (error: any) {
      console.error("Error submitting assessment:", error);
      toast.error(error.message || "Error saving assessment");
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    form,
    onSubmit,
    isSubmitting,
    id,
  };
};
